import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Button, SxProps, Theme, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Autocomplete, AutocompleteOption, UNKNOWN_OPTION_VALUE } from '~/components/Autocomplete';
import { ConfirmModal } from '~/components/ConfirmModal';
import { DateField } from '~/components/DateField';
import { TextField } from '~/components/TextField';
import { ROUTES } from '~/constants/routes';
import { useDebounce } from '~/hooks/useDebounce';
import { useNotification } from '~/hooks/useNotification';
import {
  useCheckCompanyExistenceMutation,
  useDeleteDraftCompanyMutation,
  useGetCompetitorsQuery,
  useGetFirstFinancingDealTypesQuery,
  useGetIndustriesQuery,
} from '~/services/rtkQuery/company';
import { CreateCompanyFormValues } from '~/types/formValues';
import { CompanyStatus } from '~/types/models';
import { handleCompanyFormikErrors } from '~/utils/helpers';

import { Round, RoundRef } from './components/Round';
import styles from './styles.module.scss';

const COMPANY_BIO_TEXT_FIELD_SX: SxProps<Theme> = {
  gridColumn: 'span 2',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};
const COMPETITORS_AUTOCOMPLETE_SX: SxProps<Theme> = { gridColumn: 'span 2' };
type FORM_ACTION_TYPE = 'save' | 'submit' | 'delete';

interface Props {
  companyId: number | null;
  companyPbId: string | null;
  roundRefs: React.MutableRefObject<(RoundRef | null)[]>;
  companyLocked: boolean;
  generatedLink: boolean;
  loadingSaveCompanyAsDraft: boolean;
  setExistingCompany: (company: { id: number; status: CompanyStatus } | null) => void;
  onSaveAsDraft: (submitGeneratedLink?: boolean) => Promise<void>;
}

export const Component: React.FC<Props> = ({
  companyId,
  companyPbId,
  roundRefs,
  companyLocked,
  generatedLink,
  loadingSaveCompanyAsDraft,
  setExistingCompany,
  onSaveAsDraft,
}) => {
  const navigate = useNavigate();
  const [competitorsSearchValue, setCompetitorsSearchValue] = useState('');
  const [swappingRound, setSwappingRound] = useState(false);
  const [formActionConfirmModalVisible, setFormActionConfirmModalVisible] = useState(false);
  const formActionType = useRef<FORM_ACTION_TYPE | null>(null);
  const getCompetitorsQuery = useGetCompetitorsQuery(
    {
      query: competitorsSearchValue.trim(),
    },
    { skip: competitorsSearchValue.trim().length < 3 },
  );
  const getIndustriesQuery = useGetIndustriesQuery();
  const getFirstFinancingDealTypesQuery = useGetFirstFinancingDealTypesQuery();
  const [deleteDraftCompanyMutation, { isLoading: isLoadingDeleteDraftCompanyMutation }] =
    useDeleteDraftCompanyMutation();
  const [checkCompanyExistenceMutation, { isLoading: isLoadingCheckCompanyExistenceMutation }] =
    useCheckCompanyExistenceMutation();
  const { handleApiError, showNotification } = useNotification();
  const formik = useFormikContext<CreateCompanyFormValues>();
  const debouncedFormik = useDebounce(formik);
  const previousSubmitCount = useRef(0);

  useEffect(() => {
    if (debouncedFormik.submitCount !== previousSubmitCount.current) {
      previousSubmitCount.current = debouncedFormik.submitCount;

      handleCompanyFormikErrors(
        debouncedFormik,
        debouncedFormik.errors,
        showNotification,
        roundRefs,
      );
    }
  }, [debouncedFormik, debouncedFormik.submitCount, roundRefs, showNotification]);

  const toggleRoundSwap = useCallback(() => {
    setSwappingRound(prevValue => !prevValue);
  }, []);

  const showFormActionConfirmModal = useCallback(() => {
    setFormActionConfirmModalVisible(true);
  }, []);

  const hideFormActionConfirmModal = useCallback(() => {
    setFormActionConfirmModalVisible(false);
  }, []);

  const handleActionButtonClick = useCallback(
    (type: FORM_ACTION_TYPE) => {
      formActionType.current = type;

      showFormActionConfirmModal();
    },
    [showFormActionConfirmModal],
  );

  const handleDelete = useCallback(async () => {
    try {
      if (companyId) {
        await deleteDraftCompanyMutation({ id: companyId }).unwrap();

        navigate(ROUTES.COMPANIES);
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [companyId, deleteDraftCompanyMutation, handleApiError, navigate]);

  const handleFormActionConfirm = useCallback(async () => {
    if (formActionType.current === 'delete') {
      await handleDelete();
    } else {
      try {
        if (formik.values.form.name && formik.values.form.web && !generatedLink) {
          const response = await checkCompanyExistenceMutation({
            companyName: formik.values.form.name,
            web: formik.values.form.web,
            id: companyId || undefined,
            pbId: companyPbId || undefined,
          }).unwrap();

          if (response.result.data) {
            setExistingCompany({
              id: response.result.data.latestCompanyId,
              status: response.result.data.company_status,
            });

            showNotification({ type: 'error', title: response.title, message: response.message });

            hideFormActionConfirmModal();

            return;
          }
        }

        setExistingCompany(null);

        if (formActionType.current === 'save') {
          await onSaveAsDraft();
        }

        if (formActionType.current === 'submit') {
          if (generatedLink) {
            await onSaveAsDraft(true);
          } else {
            await formik.submitForm();
          }
        }
      } catch (e) {
        handleApiError(e);
      }
    }

    hideFormActionConfirmModal();
  }, [
    hideFormActionConfirmModal,
    handleDelete,
    formik,
    generatedLink,
    setExistingCompany,
    checkCompanyExistenceMutation,
    companyId,
    companyPbId,
    showNotification,
    onSaveAsDraft,
    handleApiError,
  ]);

  const competitorOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      getCompetitorsQuery.currentData?.result.data.map(result => ({
        label: result.companyname,
        value: result.companyid,
        meta: result.pbId ? { pbId: result.pbId } : undefined,
      })) || []
    );
  }, [getCompetitorsQuery.currentData?.result]);

  const industryOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      getIndustriesQuery.data?.result.data.map(industry => ({
        label: industry.name,
        value: industry.id,
      })) || []
    );
  }, [getIndustriesQuery.data]);

  const firstFinancingDealTypeOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      getFirstFinancingDealTypesQuery.data?.result.data.map(firstFinancingDealType => ({
        label: firstFinancingDealType.name,
        value: firstFinancingDealType.id,
      })) || []
    );
  }, [getFirstFinancingDealTypesQuery.data]);

  return (
    <div>
      <div className={styles['general-info']}>
        <Typography color="text.primary" fontWeight="700" mb="25px" variant="subtitle1">
          General Info
        </Typography>
        <div className={styles['general-info__fields']}>
          <TextField
            disabled={companyLocked}
            error={formik.touched.form?.name && !!formik.errors.form?.name}
            helperText={formik.touched.form?.name ? formik.errors.form?.name : undefined}
            label="Company Name*"
            name="form.name"
            value={formik.values.form.name}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <TextField
            disabled={companyLocked}
            error={formik.touched.form?.formerName && !!formik.errors.form?.formerName}
            helperText={
              formik.touched.form?.formerName ? formik.errors.form?.formerName : undefined
            }
            label="Company Former Name"
            name="form.formerName"
            value={formik.values.form.formerName}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <TextField
            disabled={companyLocked}
            error={formik.touched.form?.web && !!formik.errors.form?.web}
            helperText={
              formik.touched.form?.web && formik.errors.form?.web ? (
                <>
                  {formik.errors.form?.web}
                  <Typography
                    color="var(--error-color)"
                    component="p"
                    lineHeight="16px"
                    variant="subtitle2"
                  >
                    (e.g.{' '}
                    <Typography
                      color="var(--main-2-color)"
                      component="span"
                      lineHeight="16px"
                      sx={{
                        textDecoration: 'underline',
                      }}
                      variant="subtitle2"
                    >
                      https://www.company.com
                    </Typography>
                    )
                  </Typography>
                </>
              ) : undefined
            }
            label="Web*"
            name="form.web"
            type="url"
            value={formik.values.form.web}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <TextField
            disabled
            error={formik.touched.form?.pitchbookUrl && !!formik.errors.form?.pitchbookUrl}
            helperText={
              formik.touched.form?.pitchbookUrl ? formik.errors.form?.pitchbookUrl : undefined
            }
            label="Pitchbook URL"
            name="form.pitchbookUrl"
            type="url"
            value={formik.values.form.pitchbookUrl}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <TextField
            disabled={companyLocked}
            error={formik.touched.form?.foundedYear && !!formik.errors.form?.foundedYear}
            helperText={
              formik.touched.form?.foundedYear ? formik.errors.form?.foundedYear : undefined
            }
            label="Year Founded*"
            name="form.foundedYear"
            type="number"
            value={formik.values.form.foundedYear}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <Autocomplete
            disableClearable
            multiple
            disabled={companyLocked}
            error={formik.touched.form?.industries && !!formik.errors.form?.industries}
            helperText={
              formik.touched.form?.industries
                ? (formik.errors.form?.industries as string)
                : undefined
            }
            options={industryOptions}
            placeholder="Industries*"
            value={formik.values.form.industries}
            onBlur={() => formik.setFieldTouched('form.industries')}
            onChange={value => formik.setFieldValue('form.industries', value)}
          />
          <DateField
            disabled={companyLocked}
            error={
              formik.touched.form?.firstFinancingDate && !!formik.errors.form?.firstFinancingDate
            }
            helperText={
              formik.touched.form?.firstFinancingDate
                ? formik.errors.form?.firstFinancingDate
                : undefined
            }
            label="First Financing Date*"
            value={formik.values.form.firstFinancingDate}
            onBlur={() => formik.setFieldTouched('form.firstFinancingDate')}
            onChange={date => formik.setFieldValue('form.firstFinancingDate', date)}
          />
          <Autocomplete
            disabled={companyLocked}
            error={
              formik.touched.form?.firstFinancingDealType &&
              !!formik.errors.form?.firstFinancingDealType
            }
            helperText={
              formik.touched.form?.firstFinancingDealType
                ? formik.errors.form?.firstFinancingDealType
                : undefined
            }
            options={firstFinancingDealTypeOptions}
            placeholder="First Financing Deal Type*"
            value={formik.values.form.firstFinancingDealType}
            onBlur={() => formik.setFieldTouched('form.firstFinancingDealType')}
            onChange={value => formik.setFieldValue('form.firstFinancingDealType', value)}
          />
          <TextField
            disabled={companyLocked}
            error={formik.touched.form?.bio && !!formik.errors.form?.bio}
            helperText={formik.touched.form?.bio ? formik.errors.form?.bio : undefined}
            label="Description"
            name="form.bio"
            sx={COMPANY_BIO_TEXT_FIELD_SX}
            title={formik.values.form.bio}
            value={formik.values.form.bio}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <Autocomplete
            creatable
            disableClearable
            multiple
            disabled={companyLocked}
            error={formik.touched.form?.competitors && !!formik.errors.form?.competitors}
            helperText={
              (formik.touched.form?.competitors && (formik.errors.form?.competitors as string)) ||
              `Total Competitors ${
                formik.values.form.competitors[0]?.value === UNKNOWN_OPTION_VALUE
                  ? 0
                  : formik.values.form.competitors.length
              }`
            }
            loading={getCompetitorsQuery.isFetching}
            options={competitorOptions}
            placeholder="Competitors*"
            sx={COMPETITORS_AUTOCOMPLETE_SX}
            unknownOptionLabel="Competitors unknown"
            value={formik.values.form.competitors}
            onBlur={() => formik.setFieldTouched('form.competitors')}
            onChange={value => formik.setFieldValue('form.competitors', value)}
            onSearchValueChange={setCompetitorsSearchValue}
          />
        </div>
      </div>
      <div className={styles.rounds}>
        <Button
          disabled={companyLocked}
          size="small"
          startIcon={<AutorenewIcon sx={{ width: '24px', height: '24px' }} />}
          sx={{
            width: '131px',
            backgroundColor: swappingRound ? undefined : 'var(--main-2-color)',
            pl: '8px',
            pr: '8px',
            fontSize: '14px',
            alignSelf: 'flex-end',
            mb: '16px',
          }}
          onClick={toggleRoundSwap}
        >
          Round Swap
        </Button>
        {formik.values.form.rounds.map((round, index) => (
          <Round
            className={
              index !== formik.values.form.rounds.length - 1 ? styles.rounds__round : undefined
            }
            companyLocked={companyLocked}
            key={round.name}
            previousRound={formik.values.form.rounds[index - 1]}
            ref={ref => {
              roundRefs.current[index] = ref;
            }}
            round={round}
            roundIndex={index}
            swapping={swappingRound}
          />
        ))}
      </div>
      <div className={styles.comments}>
        <Typography color="text.primary" fontWeight="700" mb="8px" ml="4px" variant="subtitle1">
          Comments
        </Typography>
        <TextField
          multiline
          disabled={companyLocked}
          error={formik.touched.form?.comments && !!formik.errors.form?.comments}
          helperText={formik.touched.form?.comments ? formik.errors.form?.comments : undefined}
          name="form.comments"
          value={formik.values.form.comments}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
      </div>
      <div className={styles.buttons}>
        {companyId && !generatedLink && (
          <Button
            color="secondary"
            disabled={companyLocked}
            size="small"
            sx={{
              width: '158px',
              mr: '16px',
              fontSize: '14px',
              lineHeight: 'normal',
            }}
            variant="outlined"
            onClick={() => handleActionButtonClick('delete')}
          >
            Delete
          </Button>
        )}
        <Button
          disabled={companyLocked}
          size="small"
          sx={{
            width: '158px',
            mr: '16px',
            color: 'text.primary',
            fontSize: '14px',
            lineHeight: 'normal',
            backgroundColor: 'var(--white-color)',
            border: companyLocked ? undefined : '1px solid var(--main-1-color)',
          }}
          variant="outlined"
          onClick={() => handleActionButtonClick('save')}
        >
          Save
        </Button>
        <Button
          disabled={companyLocked}
          size="small"
          sx={{ width: '173px', fontSize: '14px', lineHeight: 'normal', borderRadius: '8px' }}
          onClick={() => handleActionButtonClick('submit')}
        >
          Submit
        </Button>
      </div>
      <ConfirmModal
        description={`Are you sure you want to ${formActionType.current} this company?`}
        loading={
          isLoadingCheckCompanyExistenceMutation ||
          isLoadingDeleteDraftCompanyMutation ||
          loadingSaveCompanyAsDraft ||
          formik.isSubmitting
        }
        open={formActionConfirmModalVisible}
        title={`Do you want to ${formActionType.current}?`}
        onCancel={hideFormActionConfirmModal}
        onConfirm={handleFormActionConfirm}
      />
    </div>
  );
};

export const GeneralInfo = memo(Component);
